import { computed, Injectable, signal } from "@angular/core";
import { MaterialStateModel } from "../Dto/MaterialStateModel";
import { MaterialListItem } from "../Dto/MaterialModel";
import { PaginationInfo } from "../Dto/MaterialSearchResponse";
import { SearchMaterialRequest } from "../Dto/MaterialSearchRequest";
import { sortByMaterialSequence } from "../../../Common/Utility/UtilityMaterialSequence";

const initialState: MaterialStateModel = {
  data: [],
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  },
  searchParams: {
    page: 1,
    limit: 10,
  },
  loading: false,
  error: null,
};

@Injectable({ providedIn: "root" })
export class MaterialState {
  private readonly state = signal<MaterialStateModel>(initialState);
  public readonly data = computed(() => this.state().data);
  public readonly pagination = computed(() => this.state().pagination);
  public readonly searchParams = computed(() => this.state().searchParams);
  public readonly loading = computed(() => this.state().loading);
  public readonly error = computed(() => this.state().error);

  setMaterials(data: MaterialListItem[], pagination: PaginationInfo): void {
    this.state.update((state) => ({
      ...state,
      data: this.sortMaterials(data),
      pagination,
      loading: false,
      error: null,
    }));
  }

  setSearchParams(params: SearchMaterialRequest): void {
    this.state.update((state) => ({
      ...state,
      searchParams: params,
    }));
  }

  setLoading(loading: boolean): void {
    this.state.update((state) => ({ ...state, loading }));
  }

  setError(error: string | null): void {
    this.state.update((state) => ({ ...state, error }));
  }

  updateMaterialInState(materialId: string, materialData: Partial<MaterialListItem>): void {
    this.state.update((state) => ({
      ...state,
      data: this.sortMaterials(
        state.data.map((material) =>
          (() => {
            if (material.id === materialId) {
              return { ...material, ...materialData };
            }
            return material;
          })(),
        ),
      ),
      loading: false,
      error: null,
    }));
  }

  addMaterialToState(material: MaterialListItem): void {
    this.state.update((state) => ({
      ...state,
      data: this.sortMaterials([...state.data, material]),
      pagination: {
        ...state.pagination,
        total: state.pagination.total + 1,
      },
      loading: false,
      error: null,
    }));
  }

  removeMaterialFromState(materialId: string): void {
    this.state.update((state) => ({
      ...state,
      data: state.data.filter((material) => material.id !== materialId),
      pagination: {
        ...state.pagination,
        total: Math.max(0, state.pagination.total - 1),
      },
      loading: false,
      error: null,
    }));
  }

  materialById = computed(() => {
    const materials = this.state().data;
    return (id: string) => materials.find((material) => material.id === id) || null;
  });

  private sortMaterials(materials: MaterialListItem[]): MaterialListItem[] {
    return sortByMaterialSequence(materials, (material) => material);
  }
}
